import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';
import { getUserSubscriptionData, PLANS } from './subscriptionService';
import { logger } from './logger';

// Lazy load dos modais para melhor performance
const NoSubscriptionModal = React.lazy(() => import('../components/NoSubscriptionModal'));
const SubscriptionAlertModal = React.lazy(() => import('../components/SubscriptionAlertModal'));

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load session on mount and subscribe to changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(session);
      setLoading(false);
    })();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe?.();
    };
  }, []);

  // Fetch profile and subscription when session changes
  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      if (!session?.user) {
        setProfile(null);
        setSubscription(null);
        setSubscriptionData(null);
        return;
      }

      const userId = session.user.id;

      try {
        // Buscar dados completos do usuário e assinatura
        const data = await getUserSubscriptionData(userId);

        if (!ignore) {
          setProfile(data.user || { id: userId, email: session.user.email });
          setSubscription(data.subscription);
          setSubscriptionData(data);
        }
      } catch (error) {
        logger.error('Erro ao carregar dados do usuário:', error);

        // Fallback para dados mínimos
        if (!ignore) {
          setProfile({ id: userId, email: session.user.email });
          setSubscription(null);
          setSubscriptionData({
            user: { id: userId, email: session.user.email },
            subscription: null,
            plan: PLANS.basic,
            usage: {
              messagesThisMonth: 0,
              tokensUsed: 0,
              conversationCount: 0,
              exportsThisMonth: 0,
            },
            limits: {
              canCreateConversation: true,
              canExport: true,
              hasQuickTopics: false,
              hasPrioritySupport: false,
              hasContractTemplates: false,
              hasNewsletter: false,
              hasApiAccess: false,
            },
          });
        }
      }
    };
    fetchData();
    return () => { ignore = true; };
  }, [session]);

  // Função para recarregar dados de subscription (útil após checkout)
  const refreshSubscription = async () => {
    if (!session?.user) return;

    try {
      const data = await getUserSubscriptionData(session.user.id);
      setProfile(data.user);
      setSubscription(data.subscription);
      setSubscriptionData(data);
    } catch (error) {
      logger.error('Erro ao recarregar assinatura:', error);
    }
  };

  // Função de logout que limpa todas as preferências
  const handleSignOut = async () => {
    try {
      logger.log('🚪 Iniciando processo de logout...');

      // Limpar tema para padrão (claro)
      localStorage.setItem('theme', 'light');
      localStorage.setItem('lexfy-chat-theme', 'light');
      document.documentElement.classList.remove('dark');

      // Limpar estados IMEDIATAMENTE antes do signOut
      setSession(null);
      setProfile(null);
      setSubscription(null);
      setSubscriptionData(null);

      // Fazer logout no Supabase
      await supabase.auth.signOut();

      // Disparar evento de mudança para atualizar todos os componentes
      window.dispatchEvent(new Event('storage'));

      logger.log('✅ Logout concluído com sucesso');
    } catch (error) {
      logger.error('❌ Erro ao fazer logout:', error);

      // Mesmo com erro, limpa os estados locais
      setSession(null);
      setProfile(null);
      setSubscription(null);
      setSubscriptionData(null);
    }
  };

  const value = useMemo(() => ({
    session,
    user: session?.user || null,
    emailConfirmed: !!session?.user?.email_confirmed_at,
    profile,
    subscription,
    subscriptionData,
    plan: subscriptionData?.plan || PLANS.basic,
    usage: subscriptionData?.usage || {},
    limits: subscriptionData?.limits || {},
    loading,
    signOut: handleSignOut,
    refreshSubscription,
  }), [session, profile, subscription, subscriptionData, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Gate component to protect routes that require authentication
export const ProtectedRoute = ({ children, fallback = null }) => {
  const { user, loading, emailConfirmed } = useAuth();
  if (loading) return fallback;
  if (!user) {
    // If not authenticated, redirect via client routing
    if (typeof window !== 'undefined') {
      window.location.replace('/user-login');
    }
    return null;
  }
  if (!emailConfirmed) {
    if (typeof window !== 'undefined') {
      window.location.replace('/verify-email');
    }
    return null;
  }
  return children;
};

// Gate to ensure subscription exists and active
export const SubscriptionGate = ({ children, allowPending = false }) => {
  const { subscription, loading, user, plan } = useAuth();
  const [checkoutRecent, setCheckoutRecent] = useState(false);
  const [showNoSubModal, setShowNoSubModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [modalReason, setModalReason] = useState('none');

  // Calcular variáveis antes dos hooks (para usar nas dependências)
  const isCheckoutSuccess = typeof window !== 'undefined' &&
    window.location.pathname === '/checkout-success';
  const isActive = subscription?.status === 'active' && !subscription?.cancel_at_period_end;
  const isCanceled = subscription?.status === 'canceled';
  // Usuário com assinatura ativa mas com cancelamento agendado (vai expirar no fim do período)
  const isPendingCancellation = subscription?.status === 'active' && subscription?.cancel_at_period_end;
  // Novo usuário = nunca teve uma assinatura
  const isNewUser = !subscription;

  // Hook 1: Verificar se veio recentemente do checkout (últimos 10 minutos = 600 segundos)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const lastCheckout = sessionStorage.getItem('lastCheckoutTime');
      if (lastCheckout) {
        const timeSinceCheckout = Date.now() - parseInt(lastCheckout);
        const allowedTime = 600000; // 10 minutos

        logger.log('🕒 Tempo desde último checkout:', Math.round(timeSinceCheckout / 1000), 'segundos');

        // Permite acesso por 10 minutos após checkout
        if (timeSinceCheckout < allowedTime) {
          logger.log('✅ Acesso temporário permitido (checkout recente)');
          setCheckoutRecent(true);

          // Remove após o tempo permitido
          setTimeout(() => {
            logger.log('⏰ Tempo de acesso temporário expirado');
            setCheckoutRecent(false);
            sessionStorage.removeItem('lastCheckoutTime');
          }, allowedTime - timeSinceCheckout);
        } else {
          logger.log('⏰ Tempo de acesso temporário já expirou');
          sessionStorage.removeItem('lastCheckoutTime');
        }
      }
    }
  }, []);

  // Hook 2: Determinar ação apropriada baseada no estado da assinatura
  useEffect(() => {
    if (!loading && !isActive && !isCheckoutSuccess && !checkoutRecent && !alertDismissed) {
      const timer = setTimeout(() => {
        // Caso 1: Usuário novo (nunca teve assinatura) - mostrar modal de alerta
        if (isNewUser) {
          logger.log('🆕 SubscriptionGate: Novo usuário sem assinatura, mostrando alerta');
          setShowAlertModal(true);
        }
        // Caso 2: Assinatura cancelada - mostrar modal de cancelamento
        else if (isCanceled) {
          logger.log('❌ SubscriptionGate: Assinatura cancelada, mostrando modal');
          setModalReason('canceled');
          setShowNoSubModal(true);
        }
        // Caso 3: Assinatura com cancelamento pendente (ainda ativa até o fim do período)
        // Não deve bloquear, pois ainda está ativa
        else if (isPendingCancellation) {
          // Este caso não deveria chegar aqui porque isActive seria true
          // Mas se chegar, não bloqueia
          logger.log('⏳ SubscriptionGate: Cancelamento pendente, permitindo acesso');
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, isActive, isCanceled, isNewUser, isPendingCancellation, isCheckoutSuccess, checkoutRecent, alertDismissed]);

  // Logs para debug
  useEffect(() => {
    logger.log('🔒 SubscriptionGate: Verificando acesso', {
      hasSubscription: !!subscription,
      status: subscription?.status,
      isActive,
      isCanceled,
      isPendingCancellation,
      isNewUser,
      cancel_at_period_end: subscription?.cancel_at_period_end,
      user: user?.email,
    });
  }, [subscription, isActive, isCanceled, isPendingCancellation, isNewUser, user]);

  // Handler para fechar o modal de alerta e permitir acesso temporário
  const handleDismissAlert = () => {
    setShowAlertModal(false);
    setAlertDismissed(true);
    // Marca que foi alertado para não mostrar novamente nesta sessão
    sessionStorage.setItem('subscriptionAlertShown', 'true');
  };

  // Renderização condicional (APÓS todos os hooks)

  // Se ainda está carregando, mostra loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground font-body">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se está vindo do checkout recentemente, permite acesso enquanto aguarda webhook
  if (isCheckoutSuccess || allowPending || checkoutRecent) {
    logger.log('🚪 SubscriptionGate: Permitindo acesso (checkout recente ou página de sucesso)');
    return children;
  }

  // Se assinatura foi cancelada (não é novo usuário), mostra modal de bloqueio
  if (!isActive && isCanceled) {
    logger.warn('⛔ SubscriptionGate: Acesso negado - assinatura cancelada');

    return (
      <React.Suspense fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground font-body">Carregando...</p>
          </div>
        </div>
      }>
        <NoSubscriptionModal
          isOpen={showNoSubModal}
          reason={modalReason}
          planName={plan?.name || 'Premium'}
        />
      </React.Suspense>
    );
  }

  // Se não tem assinatura ativa (novo usuário) - mostrar modal de alerta e permitir acesso
  if (!isActive && isNewUser) {
    return (
      <React.Suspense fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground font-body">Verificando assinatura...</p>
          </div>
        </div>
      }>
        {/* Mostra o modal de alerta sobre assinatura */}
        <SubscriptionAlertModal
          isOpen={showAlertModal}
          onClose={handleDismissAlert}
        />
        {/* Renderiza o conteúdo por trás (dashboard com funcionalidade limitada) */}
        {children}
      </React.Suspense>
    );
  }

  // Acesso permitido
  logger.log('✅ SubscriptionGate: Acesso permitido');
  return children;
};
